/**
 * 🎛️ EDIT MODE HOOK
 * ════════════════════════════════════════════════════════════════════════
 * 
 * Manages EDIT mode state with Executive Immutability principles:
 * - Changes are staged locally (draft state)
 * - Backend recalculates ONLY on Save
 * - User maintains visual control during editing
 * - All changes are explicit, confirmed, and explained
 */

import { useCallback, useMemo } from 'react';
import { ExecutiveDashboard, ExecutiveWidget } from '../types';
import { useEditModeStore } from '../state/editModeStore';

export function useEditMode(originalDashboard: ExecutiveDashboard) {
  const {
    isEditing,
    draftDashboard,
    removedWidgetIds,
    hasChanges,
    enterEditMode: enterEditModeStore,
    cancelEdit: cancelEditStore,
    markWidgetForRemoval: markWidgetForRemovalStore,
    undoWidgetRemoval: undoWidgetRemovalStore,
    addDuplicatedWidget: addDuplicatedWidgetStore,
  } = useEditModeStore();

  /**
   * Enter EDIT mode
   * Creates draft copy of dashboard
   */
  const enterEditMode = useCallback(() => {
    enterEditModeStore(originalDashboard);
  }, [enterEditModeStore, originalDashboard]);

  /**
   * Exit EDIT mode without saving
   * Discards all draft changes
   */
  const cancelEdit = useCallback(() => {
    cancelEditStore();
  }, [cancelEditStore]);

  /**
   * Mark widget for removal
   * DOES NOT touch backend, only updates draft
   */
  const markWidgetForRemoval = useCallback((widgetId: string) => {
    markWidgetForRemovalStore(widgetId);
  }, [markWidgetForRemovalStore]);

  /**
   * Undo widget removal
   * Restores widget to draft
   */
  const undoWidgetRemoval = useCallback((widgetId: string) => {
    undoWidgetRemovalStore(widgetId, originalDashboard);
  }, [originalDashboard, undoWidgetRemovalStore]);

  /**
   * Add duplicated widget to draft
   * Widget already has layout from backend
   * DOES NOT move existing widgets
   */
  const addDuplicatedWidget = useCallback((newWidget: ExecutiveWidget) => {
    addDuplicatedWidgetStore(newWidget);
  }, [addDuplicatedWidgetStore]);

  /**
   * Get current dashboard for rendering
   * Returns draft if editing, original otherwise
   */
  const currentDashboard = useMemo<ExecutiveDashboard>(() => {
    return isEditing && draftDashboard ? draftDashboard : originalDashboard;
  }, [draftDashboard, isEditing, originalDashboard]);

  return {
    isEditing,
    hasChanges,
    removedWidgetIds,
    currentDashboard,
    enterEditMode,
    cancelEdit,
    markWidgetForRemoval,
    undoWidgetRemoval,
    addDuplicatedWidget
  };
}
